import type {
  CategoryNestedSelectDto,
  MissionAssessmentEffectSelectDto,
  TechnologyNestedSelectDto,
} from '@@types/api.types';
import {
  type SankeyChartConfig,
  SankeyDataBuilder,
  calculateSankeySize,
  createSankeyChartUtils,
} from '@utils/sankey.util';
import { useCallback, useMemo } from 'react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sankey, type SankeyData, type SankeyProps, type SankeySettings } from './sankey.component';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Node types for the Technology Effects
 */
type NodeType = 'category' | 'domain' | 'primary_effect' | 'secondary_effect' | 'technology' | 'use';

/**
 * Context data for technology effects nodes
 */
type NodeContext = {
  technologyId?: string;
  type: NodeType;
};

/**
 * Props for the Technology Effects Sankey component
 */
type TechnologyEffectsSankeyProps = {
  // Multiple technologies mode
  data: Array<{
    category: CategoryNestedSelectDto;
    effects: MissionAssessmentEffectSelectDto[];
    technology: TechnologyNestedSelectDto;
  }>;

  // Chart settings
  settings?: Partial<SankeySettings>;

  // Sankey props
  sankeyProps?: Partial<SankeyProps>;

  /** Optional technology ID to filter the visualization to a single technology */
  technologyId?: string;
};

type SankeyEffect = {
  domain: string;
  primaryEffect: string;
  secondaryEffects: string[];
  use: string;
};

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Configuration for Technology Effects Sankey chart
 * Defines colors and labels for each node type
 */
const TECHNOLOGY_EFFECTS_CONFIG: SankeyChartConfig<NodeType> = {
  nodeTypes: {
    category: {
      color: '#10B981', // Emerald
      label: 'Category',
    },
    domain: {
      color: '#F59E0B', // Amber
      label: 'Domain',
    },
    primary_effect: {
      color: '#8B5CF6', // Violet
      label: 'Primary Effect',
    },
    secondary_effect: {
      color: '#EC4899', // Pink
      label: 'Secondary Effect',
    },
    technology: {
      color: '#3B82F6', // Blue
      label: 'Technology',
    },
    use: {
      color: '#06B6D4', // Cyan
      label: 'Use',
    },
  },
};

// Create chart utilities using the configuration
const chartUtils = createSankeyChartUtils(TECHNOLOGY_EFFECTS_CONFIG);

// =============================================================================
// DATA PROCESSING
// =============================================================================

/**
 * Processes technology effects data to create Sankey-compatible data structure
 *
 * Creates a flow from Technology -> Category -> Use -> Domain -> Primary Effect -> Secondary Effect
 *
 * @param technologies - Array of technology data objects
 * @returns Processed Sankey data structure
 */
function processTechnologyEffectsData(
  technologies: Array<{
    category: CategoryNestedSelectDto;
    effects: MissionAssessmentEffectSelectDto[];
    technology: TechnologyNestedSelectDto;
  }>,
  technologyId?: string,
): SankeyData<Record<string, unknown>, NodeContext> {
  const builder = new SankeyDataBuilder<NodeContext>();

  const filteredTechnologies = technologies.filter((item) => !technologyId || item.technology.id === technologyId);

  if (!filteredTechnologies.length) {
    return builder.getData();
  }

  // Flatten all effects from all technologies
  const allEffects: Array<{
    category: CategoryNestedSelectDto;
    effect: MissionAssessmentEffectSelectDto;
    technology: TechnologyNestedSelectDto;
  }> = [];

  filteredTechnologies.forEach(({ technology, category, effects }) => {
    effects.forEach((effect) => {
      allEffects.push({ technology, category, effect });
    });
  });

  if (!allEffects.length) {
    return builder.getData();
  }

  const sankeyEffects: Array<SankeyEffect & { technology: string; category: string }> = allEffects.map(
    ({ technology, category, effect }) => ({
      category: category.name,
      domain: effect.domain.name,
      primaryEffect: effect.effect.name,
      secondaryEffects: effect.secondaryEffects?.map((se) => se.name) ?? [],
      technology: technology.name,
      use: effect.use.name,
    }),
  );

  // Add all unique nodes
  const uniqueTechnologies = Array.from(new Set(sankeyEffects.map((e) => e.technology).filter(Boolean)));
  const uniqueCategories = Array.from(new Set(sankeyEffects.map((e) => e.category).filter(Boolean)));
  const uniqueUses = Array.from(new Set(sankeyEffects.map((e) => e.use).filter(Boolean)));
  const uniqueDomains = Array.from(new Set(sankeyEffects.map((e) => e.domain).filter(Boolean)));
  const uniquePrimaryEffects = Array.from(new Set(sankeyEffects.map((e) => e.primaryEffect).filter(Boolean)));
  const uniqueSecondaryEffects = Array.from(
    new Set(sankeyEffects.flatMap((e) => e.secondaryEffects || []).filter(Boolean)),
  );

  // Add nodes for each level
  uniqueTechnologies.forEach((technologyName) => {
    const technologyData = filteredTechnologies.find((t) => t.technology.name === technologyName);
    builder.addNode({
      name: technologyName,
      type: 'technology',
      extraData: {
        type: 'technology',
        technologyId: technologyData?.technology.id,
      },
    });
  });

  uniqueCategories.forEach((category) => {
    builder.addNode({
      name: category,
      type: 'category',
      extraData: { type: 'category' },
    });
  });

  uniqueUses.forEach((use) => {
    builder.addNode({
      name: use,
      type: 'use',
      extraData: { type: 'use' },
    });
  });

  uniqueDomains.forEach((domain) => {
    builder.addNode({
      name: domain,
      type: 'domain',
      extraData: { type: 'domain' },
    });
  });

  uniquePrimaryEffects.forEach((effect) => {
    builder.addNode({
      name: effect,
      type: 'primary_effect',
      extraData: { type: 'primary_effect' },
    });
  });

  uniqueSecondaryEffects.forEach((secondaryEffect) => {
    builder.addNode({
      name: secondaryEffect,
      type: 'secondary_effect',
      extraData: { type: 'secondary_effect' },
    });
  });

  // Create links between levels with aggregation

  // Technology -> Category links
  const techCategoryGroups = new Map<string, Map<string, number>>();
  sankeyEffects.forEach((effect) => {
    if (effect.technology && effect.category) {
      if (!techCategoryGroups.has(effect.technology)) {
        techCategoryGroups.set(effect.technology, new Map());
      }
      // Count one per effect occurrence; do NOT multiply by number of secondary effects.
      const increment = 1;
      const current = techCategoryGroups.get(effect.technology)!.get(effect.category) || 0;
      techCategoryGroups.get(effect.technology)!.set(effect.category, current + increment);
    }
  });

  techCategoryGroups.forEach((categoryMap, technology) => {
    categoryMap.forEach((count, category) => {
      builder.addLink({
        sourceName: technology,
        sourceType: 'technology',
        targetName: category,
        targetType: 'category',
        value: count,
      });
    });
  });

  // Category -> Use links
  const categoryUseGroups = new Map<string, Map<string, number>>();
  sankeyEffects.forEach((effect) => {
    if (effect.category && effect.use) {
      if (!categoryUseGroups.has(effect.category)) {
        categoryUseGroups.set(effect.category, new Map());
      }
      // Count one per effect occurrence to avoid inflating upstream node values.
      const increment = 1;
      const current = categoryUseGroups.get(effect.category)!.get(effect.use) || 0;
      categoryUseGroups.get(effect.category)!.set(effect.use, current + increment);
    }
  });

  categoryUseGroups.forEach((useMap, category) => {
    useMap.forEach((count, use) => {
      builder.addLink({
        sourceName: category,
        sourceType: 'category',
        targetName: use,
        targetType: 'use',
        value: count,
      });
    });
  });

  // Use -> Domain links
  const useDomainGroups = new Map<string, Map<string, number>>();
  sankeyEffects.forEach((effect) => {
    if (effect.use && effect.domain) {
      if (!useDomainGroups.has(effect.use)) {
        useDomainGroups.set(effect.use, new Map());
      }
      // Count one per effect occurrence to keep upstream columns balanced.
      const increment = 1;
      const current = useDomainGroups.get(effect.use)!.get(effect.domain) || 0;
      useDomainGroups.get(effect.use)!.set(effect.domain, current + increment);
    }
  });

  useDomainGroups.forEach((domainMap, use) => {
    domainMap.forEach((count, domain) => {
      builder.addLink({
        sourceName: use,
        sourceType: 'use',
        targetName: domain,
        targetType: 'domain',
        value: count,
      });
    });
  });

  // Domain -> Effect links
  const domainEffectGroups = new Map<string, Map<string, number>>();
  sankeyEffects.forEach((effect) => {
    if (effect.domain && effect.primaryEffect) {
      if (!domainEffectGroups.has(effect.domain)) {
        domainEffectGroups.set(effect.domain, new Map());
      }
      // Count one per effect occurrence; secondary effects are handled in the next step.
      const increment = 1;
      const current = domainEffectGroups.get(effect.domain)!.get(effect.primaryEffect) || 0;
      domainEffectGroups.get(effect.domain)!.set(effect.primaryEffect, current + increment);
    }
  });

  domainEffectGroups.forEach((effectMap, domain) => {
    effectMap.forEach((count, effectName) => {
      builder.addLink({
        sourceName: domain,
        sourceType: 'domain',
        targetName: effectName,
        targetType: 'primary_effect',
        value: count,
      });
    });
  });

  // Effect -> Secondary Effect links
  const effectSecondaryGroups = new Map<string, Map<string, number>>();
  sankeyEffects.forEach((effect) => {
    if (effect.secondaryEffects && effect.secondaryEffects.length > 0 && effect.primaryEffect) {
      if (!effectSecondaryGroups.has(effect.primaryEffect)) {
        effectSecondaryGroups.set(effect.primaryEffect, new Map());
      }
      effect.secondaryEffects.forEach((secondary) => {
        const current = effectSecondaryGroups.get(effect.primaryEffect)!.get(secondary) || 0;
        effectSecondaryGroups.get(effect.primaryEffect)!.set(secondary, current + 1);
      });
    }
  });

  effectSecondaryGroups.forEach((secondaryMap, effectName) => {
    secondaryMap.forEach((count, secondaryEffect) => {
      builder.addLink({
        sourceName: effectName,
        sourceType: 'primary_effect',
        targetName: secondaryEffect,
        targetType: 'secondary_effect',
        value: count,
      });
    });
  });

  return builder.getData();
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Technology Effects Sankey Chart
 *
 * Visualizes the flow from technology through category, use, domain, effects, and secondary effects.
 * Supports multiple technologies in a single diagram.
 *
 * Flow: Technology -> Category -> Use -> Domain -> Primary Effect -> Secondary Effect
 */
const TechnologyEffectsSankeyChart: React.FC<TechnologyEffectsSankeyProps> = (props) => {
  const { data, settings, sankeyProps, technologyId } = props;
  const navigate = useNavigate();

  // Extract signature calculation into separate memoized value
  const dataSignature = useMemo(() => {
    return `${data.length}-${data.map((d) => `${d.technology.id}-${d.effects.length}`).join(',')}`;
  }, [data]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we already track data changes with the dataSignature
  const sankeyData = useMemo(() => {
    return processTechnologyEffectsData(data, technologyId);
  }, [technologyId, dataSignature, data.length]);

  // Automatically determine size preset based on data volume
  const sizePreset = calculateSankeySize(sankeyData);

  const handleNodeClick = useCallback(
    (node: NodeContext & { name: string }) => {
      if (node.technologyId && node.type === 'technology') {
        navigate(`/technology/${node.technologyId}`);
      }
    },
    [navigate],
  );

  if (!sankeyData.nodes.length || !sankeyData.links.length) {
    return null;
  }

  const finalSettings = {
    ...settings,
    colorMap: { ...chartUtils.getColorMap(), ...settings?.colorMap },
    height: settings?.height ?? 'auto',
  };

  return (
    <div className="w-full">
      <Sankey
        data={sankeyData}
        formatNodeLabel={chartUtils.formatNodeLabel}
        formatNodeSubtext={chartUtils.formatNodeSubtext}
        onNodeClick={handleNodeClick}
        size={sizePreset}
        {...sankeyProps}
        settings={finalSettings}
      />
    </div>
  );
};

export const TechnologyEffectsSankeyComponent = React.memo(TechnologyEffectsSankeyChart);
